'use client';

import { useState, useEffect } from 'react';
import BookingProgress from './BookingProgress';
import ServiceSelection from './ServiceSelection';
import BookingDetails from './BookingDetails';
import ContactInformation from './ContactInformation';
import PaymentMethod from './PaymentMethod';
import BookingConfirmation from './BookingConfirmation';
import BookingSuccess from './BookingSuccess';
import Icon from '@/components/ui/AppIcon';

interface Vehicle {
  id: string;
  name: string;
  category: string;
  capacity: string;
  image: string;
  alt: string;
  features: string[];
  basePrice: number;
}

const mockVehicles: Vehicle[] = [
{
  id: 'luxury-sedan',
  name: 'Executive Sedan',
  category: 'Luxury Sedan',
  capacity: 'Up to 3 passengers',
  image: "https://images.unsplash.com/photo-1632931093439-43a343e5a2a7",
  alt: 'Black luxury Mercedes-Benz S-Class sedan with chrome accents parked in front of modern glass building',
  features: [
  'Premium leather interior',
  'Climate control',
  'Complimentary WiFi',
  'Phone chargers',
  'Bottled water'],

  basePrice: 95
},
{
  id: 'luxury-suv',
  name: 'Executive SUV',
  category: 'Luxury SUV',
  capacity: 'Up to 6 passengers',
  image: "https://images.unsplash.com/photo-1611504301976-fa1cea55accf",
  alt: 'White Cadillac Escalade luxury SUV with tinted windows on city street at sunset',
  features: [
  'Spacious cabin',
  'Extra luggage space',
  'Premium sound system',
  'Privacy partition',
  'Refreshments'],

  basePrice: 135
},
{
  id: 'stretch-limo',
  name: 'Stretch Limousine',
  category: 'Special Events',
  capacity: 'Up to 10 passengers',
  image: "https://images.unsplash.com/photo-1586932628478-1a36d741eead",
  alt: 'Elegant white stretch limousine with chrome details parked outside luxury hotel entrance',
  features: [
  'Bar service',
  'Entertainment system',
  'Mood lighting',
  'Privacy glass',
  'Red carpet service'],

  basePrice: 195
},
{
  id: 'sprinter-van',
  name: 'Executive Sprinter',
  category: 'Group Transportation',
  capacity: 'Up to 14 passengers',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b66ce93-1766000714807.png",
  alt: 'Black Mercedes-Benz Sprinter van with executive interior and leather captain chairs',
  features: [
  'Conference seating',
  'WiFi connectivity',
  'Power outlets',
  'Luggage capacity',
  'Professional driver'],

  basePrice: 165
},
{
  id: 'party-bus',
  name: 'Luxury Party Bus',
  category: 'Special Events',
  capacity: 'Up to 20 passengers',
  image: "https://images.unsplash.com/photo-1670022182858-e501ba5dd23a",
  alt: 'Modern black party bus with LED lighting and premium entertainment system interior',
  features: [
  'Dance floor',
  'Premium sound system',
  'LED lighting',
  'Bar area',
  'Entertainment screens'],

  basePrice: 295
},
{
  id: 'luxury-van',
  name: 'Premium Van',
  category: 'Corporate Travel',
  capacity: 'Up to 8 passengers',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_112d6b6f0-1764655300611.png",
  alt: 'Silver luxury passenger van with executive seating and business amenities',
  features: [
  'Executive seating',
  'Climate zones',
  'Business amenities',
  'Privacy curtains',
  'Refreshment center'],

  basePrice: 145
}];


export default function BookingSystemInteractive() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [bookingReference, setBookingReference] = useState('');
  const [isHydrated, setIsHydrated] = useState(false);

  const [formData, setFormData] = useState({
    serviceType: '',
    pickupDate: '',
    pickupTime: '',
    pickupLocation: '',
    dropoffLocation: '',
    passengers: '',
    specialRequests: ''
  });

  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    accountType: 'personal'
  });

  const [paymentData, setPaymentData] = useState({
    paymentMethod: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    billingZip: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const steps = [
  'Select Vehicle',
  'Trip Details',
  'Contact Info',
  'Payment',
  'Confirmation'];


  const handleUpdateFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateContactData = (field: string, value: string) => {
    setContactData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdatePaymentData = (field: string, value: string) => {
    setPaymentData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleConfirmBooking = () => {
    const reference = `DET${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    setBookingReference(reference);
    setCurrentStep(steps.length);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedVehicle !== null;
      case 1:
        return (
          formData.serviceType &&
          formData.pickupDate &&
          formData.pickupTime &&
          formData.pickupLocation &&
          formData.dropoffLocation &&
          formData.passengers);

      case 2:
        return (
          contactData.firstName &&
          contactData.lastName &&
          contactData.email &&
          contactData.phone);

      case 3:
        return paymentData.paymentMethod !== '';
      case 4:
        return true;
      default:
        return false;
    }
  };

  const selectedVehicleData = mockVehicles.find((v) => v.id === selectedVehicle);
  const estimatedPrice = selectedVehicleData ? selectedVehicleData.basePrice * 1.5 : 0;

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:236:6" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="236" data-component-end-line="241" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-16%22%7D" className="min-h-screen bg-background pt-24 pb-16">
        <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:237:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="237" data-component-end-line="240" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-6xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:238:10" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="238" data-component-end-line="238" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20animate-pulse%20rounded%20mb-8%22%7D" className="h-8 bg-muted animate-pulse rounded mb-8" />
          <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:239:10" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="239" data-component-end-line="239" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20animate-pulse%20rounded-lg%22%7D" className="h-64 bg-muted animate-pulse rounded-lg" />
        </div>
      </div>);

  }

  if (currentStep === steps.length) {
    return (
      <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:247:6" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="247" data-component-end-line="254" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-16%22%7D" className="min-h-screen bg-background pt-24 pb-16">
        <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:248:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="248" data-component-end-line="253" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-6xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <BookingSuccess data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:249:10" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="249" data-component-end-line="251" data-component-file="BookingSystemInteractive.tsx" data-component-name="BookingSuccess" data-component-content="%7B%22elementName%22%3A%22BookingSuccess%22%7D"
            bookingReference={bookingReference}
            contactEmail={contactData.email} />

        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:259:4" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="259" data-component-end-line="352" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
      <BookingProgress data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:260:6" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="260" data-component-end-line="260" data-component-file="BookingSystemInteractive.tsx" data-component-name="BookingProgress" data-component-content="%7B%22elementName%22%3A%22BookingProgress%22%7D" currentStep={currentStep} steps={steps} />

      <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:262:6" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="262" data-component-end-line="296" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-executive%20p-6%20sm%3Ap-8%22%7D" className="bg-card rounded-lg shadow-executive p-6 sm:p-8">
        {currentStep === 0 &&
        <ServiceSelection data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:264:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="264" data-component-end-line="267" data-component-file="BookingSystemInteractive.tsx" data-component-name="ServiceSelection" data-component-content="%7B%22elementName%22%3A%22ServiceSelection%22%7D"
          vehicles={mockVehicles}
          selectedVehicle={selectedVehicle}
          onSelectVehicle={setSelectedVehicle} />

        }

        {currentStep === 1 &&
        <BookingDetails data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:272:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="272" data-component-end-line="272" data-component-file="BookingSystemInteractive.tsx" data-component-name="BookingDetails" data-component-content="%7B%22elementName%22%3A%22BookingDetails%22%7D" formData={formData} onUpdateFormData={handleUpdateFormData} />
        }

        {currentStep === 2 &&
        <ContactInformation data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:276:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="276" data-component-end-line="278" data-component-file="BookingSystemInteractive.tsx" data-component-name="ContactInformation" data-component-content="%7B%22elementName%22%3A%22ContactInformation%22%7D"
          contactData={contactData}
          onUpdateContactData={handleUpdateContactData} />

        }

        {currentStep === 3 &&
        <PaymentMethod data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:283:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="283" data-component-end-line="283" data-component-file="BookingSystemInteractive.tsx" data-component-name="PaymentMethod" data-component-content="%7B%22elementName%22%3A%22PaymentMethod%22%7D" paymentData={paymentData} onUpdatePaymentData={handleUpdatePaymentData} />
        }

        {currentStep === 4 &&
        <BookingConfirmation data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:287:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="287" data-component-end-line="293" data-component-file="BookingSystemInteractive.tsx" data-component-name="BookingConfirmation" data-component-content="%7B%22elementName%22%3A%22BookingConfirmation%22%7D"
          selectedVehicle={selectedVehicle}
          vehicles={mockVehicles}
          formData={formData}
          contactData={contactData}
          paymentData={paymentData}
          estimatedPrice={estimatedPrice} />

        }
      </div>

      <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:298:6" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="298" data-component-end-line="339" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
        <button data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:299:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="299" data-component-end-line="310" data-component-file="BookingSystemInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Back%22%7D"
          onClick={handleBack}
          disabled={currentStep === 0}
          className={`flex items-center px-6 py-3 font-montserrat font-semibold rounded-md transition-smooth ${
          currentStep === 0 ?
          'bg-muted text-muted-foreground cursor-not-allowed' :
          'bg-card text-foreground border-2 border-border hover:border-primary hover:text-primary shadow-executive'}`
          }>

          <Icon data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:308:10" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="308" data-component-end-line="308" data-component-file="BookingSystemInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%2C%22className%22%3A%22mr-2%22%7D" name="ChevronLeftIcon" size={20} className="mr-2" />
          Back
        </button>

        {currentStep < steps.length - 1 ?
        <button data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:313:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="313" data-component-end-line="324" data-component-file="BookingSystemInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Continue%22%7D"
          onClick={handleNext}
          disabled={!canProceed()}
          className={`flex items-center px-6 py-3 font-montserrat font-semibold rounded-md transition-smooth ${
          canProceed() ?
          'bg-primary text-primary-foreground shadow-cta hover:bg-opacity-90' :
          'bg-muted text-muted-foreground cursor-not-allowed'}`
          }>

            Continue
            <Icon data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:323:12" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="323" data-component-end-line="323" data-component-file="BookingSystemInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%2C%22className%22%3A%22ml-2%22%7D" name="ChevronRightIcon" size={20} className="ml-2" />
          </button> :

        <button data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:326:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="326" data-component-end-line="337" data-component-file="BookingSystemInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Confirm%20Booking%22%7D"
          onClick={handleConfirmBooking}
          disabled={!canProceed()}
          className={`flex items-center px-8 py-3 font-montserrat font-semibold rounded-md transition-smooth ${
          canProceed() ?
          'bg-texas-crimson text-white shadow-cta hover:bg-opacity-90' :
          'bg-muted text-muted-foreground cursor-not-allowed'}`
          }>

            <Icon data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:335:12" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="335" data-component-end-line="335" data-component-file="BookingSystemInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22mr-2%22%7D" name="CheckCircleIcon" size={20} className="mr-2" />
            Confirm Booking
          </button>
        }
      </div>

      <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:341:6" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="341" data-component-end-line="351" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-muted%20rounded-lg%20p-4%22%7D" className="bg-muted rounded-lg p-4">
        <div data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:342:8" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="342" data-component-end-line="350" data-component-file="BookingSystemInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20text-sm%20text-muted-foreground%22%7D" className="flex items-center justify-center text-sm text-muted-foreground">
          <Icon data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:343:10" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="343" data-component-end-line="343" data-component-file="BookingSystemInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShieldCheckIcon%22%2C%22className%22%3A%22text-success%20mr-2%22%7D" name="ShieldCheckIcon" size={20} className="text-success mr-2" />
          <span data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:344:10" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="344" data-component-end-line="349" data-component-file="BookingSystemInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Secure%20booking%20powered%20by%20SSL%20encryption%20%7C%20Available%2024%2F7%20at%22%7D">
            Secure booking powered by SSL encryption | Available 24/7 at{' '}
            <a data-component-id="src/app/booking-system/components/BookingSystemInteractive.tsx:346:12" data-component-path="src/app/booking-system/components/BookingSystemInteractive.tsx" data-component-line="346" data-component-end-line="348" data-component-file="BookingSystemInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22tel%3A%2B12145550199%22%2C%22className%22%3A%22text-primary%20hover%3Aunderline%20font-medium%22%2C%22textContent%22%3A%22(214)%20555-0199%22%7D" href="tel:+12145550199" className="text-primary hover:underline font-medium">
              (214) 555-0199
            </a>
          </span>
        </div>
      </div>
    </div>);

}